import React, {useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {CPEMetaData, CPEToCreate} from '../app/types'
import {useForm, useWatch} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import TextInput, {MaskProps} from '../common/form/TextInput'
import FormRow from '../common/form/FormRow'
import FormFieldBox from '../common/form/FormFieldBox'
import SpinnableSubmitButton from '../common/form/SpinnableSubmitButton'
import {useFormSubmission} from '../common/servercall/useFormSubmission'
import ErrorAlert from '../common/components/ErrorAlert'
import FormGroup from '../common/form/FormGroup'
import CancelButton from '../common/form/CancelButton'
import FormContainer from '../common/form/FormContainer'
import FormButtons from '../common/form/FormButtons'
import {useSelector} from "react-redux";
import {selectedSiteSelector} from '../authentication/redux'
import {api} from "../common/api";
import {formatCPEModel} from "../common/util/formatters";
import {getCreatableCPEMetaData} from "./util/CPEHelpers";

const schema = () =>
    yup
        .object()
        .shape({
            model: yup.string().required('required-m'),
            name: yup.string().trim().max(30, 'too-long').nullable(),
            ipei: yup.string()
                .when('model', {
                    is: (model: string) => getCreatableCPEMetaData(model)?.needsIPEI,
                    then: schema => yup.string().trim().required('required-m')
                        .matches(/^[0-9A-Fa-f]{10}$/, {
                            message: 'invalid-ipei',
                            excludeEmptyString: true,
                        }).nullable(),
                    otherwise: schema => yup.string().trim().nullable(),
                })
            ,
            macAddress: yup.string()
                .when('model', {
                    is: (model: string) => getCreatableCPEMetaData(model)?.needsMacAddress,
                    then: schema => yup.string().trim().required('required-m')
                        .matches(/^[0-9A-Fa-f]{2}:[0-9A-Fa-f]{2}:[0-9A-Fa-f]{2}:[0-9A-Fa-f]{2}:[0-9A-Fa-f]{2}:[0-9A-Fa-f]{2}$/, {
                            message: 'invalid-mac-address',
                            excludeEmptyString: true,
                        }).nullable(),
                    otherwise: schema => yup.string().trim().nullable(),
                })
            ,
        })
        .required()

type Props = {
    cpe: CPEToCreate
    onSubmit: (cpe: CPEToCreate) => void
    readonly?: boolean
}

const macProps: MaskProps = {
    mask: '##:##:##:##:##:##',
    prepare: function (str) {
        return str.toUpperCase();
    },
    definitions: {
        '#': /[0-9a-fA-F]/,
    },
}

const CPEFormScreen = ({cpe, onSubmit, readonly}: Props) => {
    const {t} = useTranslation()
    const site = useSelector(selectedSiteSelector)
    const [CPEMetaData, setCPEMetaData] = useState<CPEMetaData | null>(null)

    const modelOptionParams = useMemo(() => {
        return {
            getOptions: async () => {
                if (!site) return []
                const { data } = await api.get(`/api/site/${site.productId}/cpe/creatable-models`)
                return data as string[]
            },
            getOptionLabel: (item: any) => formatCPEModel(item),
            getOptionValue: (item: string) => item,
            isOptionEqualToValue: (option: string, value: string) => option === value,
            noOptionsText: '',
            defaultObject: '',
        }
    }, [site])

    const {control, handleSubmit, setError} = useForm<CPEToCreate>({
        resolver: yupResolver(schema()),
        defaultValues: {
            ...cpe,
        },
    })

    const {submit, isSubmitting, serverError, onError} = useFormSubmission<CPEToCreate>(onSubmit, setError)

    const model = useWatch({
        name: 'model',
        control,
    })

    useEffect(() => {
        setCPEMetaData(getCreatableCPEMetaData(model))
    }, [model])

    return (
        <>
            <ErrorAlert errorKey={typeof serverError === 'string' ? serverError : undefined} showAlert={!!serverError}/>
            <FormContainer onSubmit={handleSubmit(submit, onError)}>
                <FormGroup label={t('ivrs.edit.section.info')} fullWidth={true}>
                    <FormRow>
                        <FormFieldBox>
                            <TextInput
                                readonly={readonly}
                                label={t('cpes.table.type')}
                                name={'model'}
                                control={control}
                                required={true}
                                autoCompleteOptions={modelOptionParams}
                                autofocus={true}
                            />
                        </FormFieldBox>
                        <FormFieldBox>
                            <TextInput readonly={readonly} label={t('cpes.table.name')}
                                       name={'name'}
                                       control={control} required={false}/>
                        </FormFieldBox>
                    </FormRow>
                    <FormRow>

                        {CPEMetaData && CPEMetaData.needsMacAddress &&
                            <FormFieldBox>
                                <TextInput readonly={readonly} label={t('cpes.table.mac-address')} name={'macAddress'}
                                           control={control} required={true} maskProps={macProps}/>
                            </FormFieldBox>
                        }
                        {CPEMetaData && CPEMetaData.needsIPEI &&
                            <FormFieldBox>
                                <TextInput readonly={readonly} label={'IPEI'} name={'ipei'}
                                           control={control} required={true}/>
                            </FormFieldBox>
                        }
                    </FormRow>
                </FormGroup>

                {readonly ? <></> : <FormButtons fullWidth={true}
                    buttons={[<SpinnableSubmitButton label={t('general.form-create')} showSpinner={isSubmitting}/>, <CancelButton/>]}/>}
            </FormContainer>
        </>
    )
}

export default CPEFormScreen
